/**
 * Drag Pixel around the hero and, if you keep dragging, his copies show up: they fly in from the sides one by
 * one (up to 8 → 9 drones in total). They don't follow him — each has its own personality and goes wild:
 * racing laps, barrel rolls, bouncing off the floor, chasing each other, dancing in pairs, hiding and peeking,
 * sneaking up to boop Pixel, poking around the page. They bump into each other (and into him) as they go.
 * Let go and they all scatter off to the sides.
 *
 * Pure simulation in world units (z = 0 plane); the scene renders a clone of the drone per kid.
 */

export type Personality = 'explorer' | 'zoomer' | 'spinner' | 'bouncer' | 'chaser' | 'shy' | 'dancer' | 'prankster';

export interface Kid {
  x: number;
  y: number;
  vx: number;
  vy: number;
  /** 0..1 pop-in scale */
  pop: number;
  out: boolean;
  /** Exit direction (-1 left / 1 right) */
  side: number;
  seed: number;
  personality: Personality;
  /** What it's doing right now — mostly its personality, sometimes a short whim */
  act: Personality;
  actUntil: number;
  /** Steering stiffness / damping — every kid has its own temperament */
  k: number;
  c: number;
  /** Current destination */
  tx: number;
  ty: number;
  nextGoalAt: number;
  /** Index of the kid it's chasing / dancing with (-1: none) */
  partner: number;
  /** Extra rotations for tricks (rad) — read by the renderer */
  roll: number;
  flip: number;
  spinRate: number;
  /** Squash on bounces / bumps, 0..1 */
  squash: number;
  /** Depth wander, world units */
  z: number;
  bornAt: number;
}

export interface SwarmBounds {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
}

/** Keep dragging this long before the first kid shows up (s) */
const FIRST_KID_AFTER = 1.6;
/** …then one more every this many seconds */
const KID_EVERY = 0.85;
export const MAX_KIDS = 8;

/** One of each, in a shuffled order, so every swarm has a full cast */
const CAST: Personality[] = ['zoomer', 'prankster', 'spinner', 'bouncer', 'chaser', 'dancer', 'shy', 'explorer'];

const rand = (a: number, b: number) => a + Math.random() * (b - a);
const clamp = (v: number, a: number, b: number) => Math.min(b, Math.max(a, v));

function shuffled<T>(list: T[]): T[] {
  const a = [...list];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

export class DroneSwarm {
  dragging = false;
  kids: Kid[] = [];
  private nextSpawnAt = 0;
  private spawned = 0;
  private cast: Personality[] = CAST;

  begin(t: number) {
    this.dragging = true;
    this.nextSpawnAt = t + FIRST_KID_AFTER;
    this.spawned = 0;
    this.cast = shuffled(CAST);
  }

  /** Let go: every kid scatters off to its nearest side */
  end() {
    this.dragging = false;
    for (const kid of this.kids) {
      kid.out = true;
      kid.side = Math.sign(kid.x) || (Math.random() < 0.5 ? -1 : 1);
    }
  }

  update(t: number, dt: number, lead: { x: number; y: number }, spacing: number, view: { w: number; h: number }, b: SwarmBounds) {
    // Spawn from alternating sides while the drag lasts
    if (this.dragging && this.kids.filter((k) => !k.out).length < MAX_KIDS && t >= this.nextSpawnAt) {
      this.nextSpawnAt = t + KID_EVERY * rand(0.8, 1.25);
      const side = this.spawned % 2 === 0 ? (Math.random() < 0.5 ? -1 : 1) : -Math.sign(this.kids[this.kids.length - 1]?.side || 1);
      const personality = this.cast[this.spawned % this.cast.length];
      this.spawned += 1;
      this.kids.push({
        x: side * (view.w + spacing),
        y: rand(b.minY, b.maxY),
        vx: -side * rand(3, 5),
        vy: rand(-1, 1),
        pop: 0,
        out: false,
        side,
        seed: Math.random() * 100,
        personality,
        act: personality,
        actUntil: t + rand(4, 8),
        k: rand(5, 11),
        c: rand(2.6, 4.2),
        tx: rand(b.minX, b.maxX),
        ty: rand(b.minY, b.maxY),
        nextGoalAt: t,
        partner: -1,
        roll: 0,
        flip: 0,
        spinRate: 0,
        squash: 0,
        z: 0,
        bornAt: t,
      });
    }

    const step = Math.min(dt, 1 / 30);
    const w = b.maxX - b.minX;
    const h = b.maxY - b.minY;
    const cx = (b.minX + b.maxX) / 2;
    const cy = (b.minY + b.maxY) / 2;
    const active = this.kids.filter((k) => !k.out);

    this.kids.forEach((kid, idx) => {
      kid.pop = Math.min(1, kid.pop + step / 0.3);
      kid.squash *= Math.exp(-step * 6);

      if (kid.out) {
        // Scatter: zoom off to the side, a little upward flutter
        kid.vx += kid.side * 22 * step;
        kid.vy += Math.sin(t * 6 + kid.seed) * 4 * step;
        kid.vx *= Math.exp(-step * 0.6);
        kid.vy *= Math.exp(-step * 2);
        kid.roll *= Math.exp(-step * 4);
        kid.flip *= Math.exp(-step * 4);
        kid.x += kid.vx * step;
        kid.y += kid.vy * step;
        return;
      }

      // Every so often a short whim (anyone can suddenly do a trick or go bother someone), then back to type
      if (t > kid.actUntil) {
        const whim = kid.act === kid.personality && Math.random() < 0.45;
        kid.act = whim ? (['spinner', 'prankster', 'chaser', 'zoomer', 'dancer'] as Personality[])[Math.floor(Math.random() * 5)] : kid.personality;
        kid.actUntil = t + (whim ? rand(1.8, 3.2) : rand(4, 8));
        kid.partner = -1;
        kid.nextGoalAt = t;
      }

      const s = kid.seed;
      let ax = 0;
      let ay = 0;
      let k = kid.k;
      let c = kid.c;
      let gravity = 0;
      const pickPartner = () => {
        if (kid.partner >= 0 && this.kids[kid.partner] && !this.kids[kid.partner].out && kid.partner !== idx) return this.kids[kid.partner];
        const others = active.filter((o) => o !== kid);
        if (!others.length) return null;
        const o = others[Math.floor(Math.random() * others.length)];
        kid.partner = this.kids.indexOf(o);
        return o;
      };

      switch (kid.act) {
        case 'explorer': {
          // Pokes around: flies to a spot, hovers there inspecting it with tiny circles, moves on
          if (t > kid.nextGoalAt) {
            kid.tx = rand(b.minX, b.maxX);
            kid.ty = rand(b.minY, b.maxY);
            kid.nextGoalAt = t + rand(1.6, 3);
          }
          kid.tx += Math.cos(t * 5 + s) * spacing * 0.6 * step;
          kid.ty += Math.sin(t * 5 + s) * spacing * 0.6 * step;
          kid.flip = Math.sin(t * 2.2 + s) * 0.35; // peers down, up
          break;
        }
        case 'zoomer': {
          // Races figure-eight laps across the whole hero, banking hard
          const p = t * (0.9 + (s % 0.3)) + s;
          kid.tx = cx + Math.sin(p) * w * 0.46;
          kid.ty = cy + Math.sin(p * 2) * h * 0.4;
          k = 14;
          c = 3;
          break;
        }
        case 'spinner': {
          // Hovers, spins up into barrel rolls and flips, then darts off somewhere new
          if (t > kid.nextGoalAt) {
            kid.tx = rand(b.minX, b.maxX);
            kid.ty = rand(b.minY, b.maxY);
            kid.nextGoalAt = t + rand(1.2, 2.2);
            kid.spinRate = (Math.random() < 0.5 ? -1 : 1) * rand(9, 16);
          }
          kid.spinRate *= Math.exp(-step * 0.9);
          // Alternates barrel rolls and front flips between darts
          if (Math.floor(kid.nextGoalAt) % 2 === 0) kid.roll += kid.spinRate * step;
          else kid.flip += kid.spinRate * 0.6 * step;
          break;
        }
        case 'bouncer': {
          // Gravity on: drops, bounces off the floor and walls, kicks itself back up when it settles
          gravity = 9;
          k = 0;
          c = 0.15;
          if (kid.y <= b.minY + 0.001 && Math.abs(kid.vy) < 1.2) {
            kid.vy = rand(5, 8);
            kid.vx += rand(-2.5, 2.5);
          }
          kid.roll += kid.vx * 0.8 * step;
          break;
        }
        case 'chaser': {
          // Picks someone and chases them — the chased one notices and flees (see below)
          const o = pickPartner();
          if (o) {
            kid.tx = o.x;
            kid.ty = o.y;
            k = 10;
            c = 2.4;
          }
          break;
        }
        case 'shy': {
          // Hides by an edge and peeks; darts to the other side if anyone gets close (Pixel included)
          if (t > kid.nextGoalAt) {
            const left = Math.random() < 0.5;
            kid.tx = left ? b.minX : b.maxX;
            kid.ty = rand(b.minY, b.maxY);
            kid.nextGoalAt = t + rand(3, 5);
          }
          const peek = Math.max(0, Math.sin(t * 1.4 + s));
          kid.tx += (kid.tx < cx ? 1 : -1) * peek * spacing * 0.02;
          const near = [lead, ...active.filter((o) => o !== kid)].some((o) => Math.hypot(o.x - kid.x, o.y - kid.y) < spacing * 1.6);
          if (near) {
            kid.tx = kid.x < cx ? b.maxX : b.minX;
            kid.nextGoalAt = t + rand(2, 3.5);
            kid.squash = 0.6;
          }
          k = 9;
          break;
        }
        case 'dancer': {
          // Grabs a partner and the two orbit each other, swapping direction now and then
          const o = pickPartner();
          if (o) {
            const dir = Math.sin(t * 0.5 + s) > 0 ? 1 : -1;
            const a = t * 3.2 * dir + s;
            const mx = (kid.x + o.x) / 2;
            const my = (kid.y + o.y) / 2;
            kid.tx = mx + Math.cos(a) * spacing * 0.75;
            kid.ty = my + Math.sin(a) * spacing * 0.75;
            // Pull the partner into the dance too
            o.vx += (mx - Math.cos(a) * spacing * 0.75 - o.x) * 2.5 * step;
            o.vy += (my - Math.sin(a) * spacing * 0.75 - o.y) * 2.5 * step;
            kid.roll = Math.sin(t * 6 + s) * 0.5;
          }
          k = 12;
          break;
        }
        case 'prankster': {
          // Sneaks up behind Pixel, boops him, bolts — repeat
          const d = Math.hypot(lead.x - kid.x, lead.y - kid.y);
          if (t > kid.nextGoalAt) {
            // Fleeing done: go sneak again
            kid.tx = lead.x - Math.sign(lead.x - kid.x || 1) * spacing * 0.6;
            kid.ty = lead.y + spacing * 0.2;
            k = 6;
            if (d < spacing * 0.9) {
              // Boop!
              kid.vx += Math.sign(kid.x - lead.x || 1) * 7;
              kid.vy += rand(2, 5);
              kid.squash = 1;
              kid.spinRate = rand(10, 14);
              const a = Math.random() * Math.PI * 2;
              kid.tx = clamp(lead.x + Math.cos(a) * w * 0.45, b.minX, b.maxX);
              kid.ty = clamp(lead.y + Math.sin(a) * h * 0.45, b.minY, b.maxY);
              kid.nextGoalAt = t + rand(1.4, 2.4);
            }
          }
          kid.spinRate *= Math.exp(-step * 2);
          kid.roll += kid.spinRate * step;
          break;
        }
      }

      // Being chased: flee from any chaser that's after this kid
      for (const o of active) {
        if (o === kid || o.act !== 'chaser' || this.kids[o.partner] !== kid) continue;
        const dx = kid.x - o.x;
        const dy = kid.y - o.y;
        const d = Math.hypot(dx, dy) || 1;
        if (d < spacing * 3) {
          ax += (dx / d) * 14;
          ay += (dy / d) * 14;
        }
        // Tag! Roles swap
        if (d < spacing * 0.7) {
          o.partner = -1;
          o.act = o.personality === 'chaser' ? 'zoomer' : o.personality;
          o.actUntil = t + rand(1.5, 2.5);
          kid.act = 'chaser';
          kid.partner = this.kids.indexOf(o);
          kid.actUntil = t + rand(2.5, 4);
          kid.squash = 1;
        }
      }

      // Little personal bob so nobody is ever perfectly still
      const tx = clamp(kid.tx + Math.sin(t * (1.3 + (s % 1)) + s) * spacing * 0.12, b.minX, b.maxX);
      const ty = clamp(kid.ty + Math.cos(t * (1.7 + (s % 0.7)) + s * 1.3) * spacing * 0.12, b.minY, b.maxY);
      kid.vx += ((tx - kid.x) * k - kid.vx * c + ax) * step;
      kid.vy += ((ty - kid.y) * k - kid.vy * c + ay - gravity) * step;
      kid.x += kid.vx * step;
      kid.y += kid.vy * step;
      kid.z = Math.sin(t * 1.1 + s) * 0.25;

      // Tricks unwind back to upright when not actively spinning
      if (kid.act !== 'spinner' && kid.act !== 'bouncer' && kid.act !== 'prankster') {
        kid.roll += (Math.round(kid.roll / (Math.PI * 2)) * Math.PI * 2 - kid.roll) * (1 - Math.exp(-step * 4));
      }
      if (kid.act !== 'spinner' && kid.act !== 'explorer') {
        kid.flip += (Math.round(kid.flip / (Math.PI * 2)) * Math.PI * 2 - kid.flip) * (1 - Math.exp(-step * 4));
      }

      // Walls and floor: bounce (with a squash)
      const bounce = kid.act === 'bouncer' ? 0.82 : 0.5;
      if (kid.x < b.minX) {
        kid.x = b.minX;
        kid.vx = Math.abs(kid.vx) * bounce;
        kid.squash = Math.max(kid.squash, Math.min(1, Math.abs(kid.vx) * 0.15));
      } else if (kid.x > b.maxX) {
        kid.x = b.maxX;
        kid.vx = -Math.abs(kid.vx) * bounce;
        kid.squash = Math.max(kid.squash, Math.min(1, Math.abs(kid.vx) * 0.15));
      }
      if (kid.y < b.minY) {
        kid.y = b.minY;
        kid.vy = Math.abs(kid.vy) * bounce;
        kid.squash = Math.max(kid.squash, Math.min(1, Math.abs(kid.vy) * 0.15));
      } else if (kid.y > b.maxY) {
        kid.y = b.maxY;
        kid.vy = -Math.abs(kid.vy) * bounce;
      }
    });

    // Bumps: drones knock each other (and Pixel) apart instead of overlapping
    const bodies = active;
    const minD = spacing * 0.78;
    for (let i = 0; i < bodies.length; i++) {
      const a = bodies[i];
      for (let j = i + 1; j < bodies.length; j++) {
        const o = bodies[j];
        const dx = o.x - a.x;
        const dy = o.y - a.y;
        const d = Math.hypot(dx, dy);
        if (d > 0.0001 && d < minD) {
          const nx = dx / d;
          const ny = dy / d;
          const push = (minD - d) / 2;
          a.x -= nx * push;
          a.y -= ny * push;
          o.x += nx * push;
          o.y += ny * push;
          const rel = (o.vx - a.vx) * nx + (o.vy - a.vy) * ny;
          if (rel < 0) {
            a.vx += rel * nx;
            a.vy += rel * ny;
            o.vx -= rel * nx;
            o.vy -= rel * ny;
            const hit = Math.min(1, -rel * 0.2);
            a.squash = Math.max(a.squash, hit);
            o.squash = Math.max(o.squash, hit);
          }
        }
      }
      // Pixel is solid too: they bounce off him
      const dx = a.x - lead.x;
      const dy = a.y - lead.y;
      const d = Math.hypot(dx, dy);
      if (d > 0.0001 && d < minD) {
        a.x = lead.x + (dx / d) * minD;
        a.y = lead.y + (dy / d) * minD;
        const rel = a.vx * (dx / d) + a.vy * (dy / d);
        if (rel < 0) {
          a.vx -= 1.6 * rel * (dx / d);
          a.vy -= 1.6 * rel * (dy / d);
          a.squash = Math.max(a.squash, 0.7);
        }
      }
    }

    // Gone once well off screen
    this.kids = this.kids.filter((k) => !(k.out && Math.abs(k.x) > view.w + spacing * 2.5));
  }

  reset() {
    this.dragging = false;
    this.kids = [];
  }
}
